import './Breadcrumb.scss';
import clsx from 'clsx';
import React, {
  ComponentPropsWithoutRef,
  ElementType,
  memo,
  PropsWithChildren,
  ReactNode,
  useMemo,
} from 'react';
import { FormattedMessage } from 'src/migration/react-intl';
import { MarkDoneIcon } from 'src/modules/common/icons/MarkDoneIcon';
import { MarkEmptyIcon } from 'src/modules/common/icons/MarkEmptyIcon';
import { MarkInProgressIcon } from 'src/modules/common/icons/MarkInProgressIcon';

type PolymorphicProps<E extends ElementType> = PropsWithChildren<ComponentPropsWithoutRef<E> & {
  as?: E;
}>;

export type BreadcrumbProps<T extends React.ElementType> = PolymorphicProps<T> & {
  title: string;
  value: ReactNode;
  isEmpty: boolean;
  isFilled: boolean;
  selected?: boolean;
  disabled?: boolean;
};

// eslint-disable-next-line react/function-component-definition
function BreadcrumbWithoutMemo<T extends ElementType = 'button'>({
  as,
  title,
  value,
  isEmpty,
  isFilled,
  selected,
  ...props
}: BreadcrumbProps<T>): React.JSX.Element {
  const Component = as || 'button';

  const stateIcon = useMemo(() => {
    if (isEmpty) {
      return <MarkEmptyIcon/>;
    }

    if (isFilled) {
      return <MarkDoneIcon/>;
    }

    return <MarkInProgressIcon/>;
  }, [isEmpty, isFilled]);

  return (
    <Component
      className={clsx({
        'bp-breadcrumb': true,
        'bp-breadcrumb--selected': selected,
        'bp-breadcrumb--disabled': props.disabled,
        'bp-breadcrumb--empty': isEmpty,
      })}
      {...props}
    >
      <div className={clsx({
        'bp-breadcrumb__status-mark': true,
        'bp-breadcrumb__status-mark--empty': isEmpty,
      })}
      >
        {stateIcon}
      </div>

      <div className="bp-breadcrumb__contents">
        <p
          className="bp-breadcrumb__title"
        ><FormattedMessage id={title}/>
        </p>

        <p className="bp-breadcrumb__value">{value}</p>
      </div>
    </Component>
  );
}

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const Breadcrumb = memo(BreadcrumbWithoutMemo) as typeof BreadcrumbWithoutMemo;
